/**
 * CommsPanel — admin bulk communications + driver recruitment (P1).
 *
 * Strict isolation: pure UI over the /admin/comms/* and /admin/recruitment/*
 * endpoints. No dispatch / lifecycle / fare logic touched. Opted-out
 * recipients are excluded server-side; this UI surfaces the reach + exclusions
 * before sending, supports immediate or scheduled sends, and lists history.
 */
import React, { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../../components/ui/dialog";
import { Checkbox } from "../../components/ui/checkbox";
import { toast } from "sonner";
import {
  MessageSquare, Mail, Send, Users, Clock, RefreshCw, Megaphone, Eye, XCircle,
  ShieldAlert, AlertTriangle, FlaskConical, Ban,
} from "lucide-react";

const AUDIENCES = [
  { value: "all", label: "All drivers" },
  { value: "approved", label: "Approved drivers" },
  { value: "pending", label: "Pending drivers" },
  { value: "online", label: "Online drivers" },
];

const STATUS_STYLE = {
  sent:      { bg: "#ECFDF5", fg: "#065F46", label: "SENT" },
  sending:   { bg: "#EFF6FF", fg: "#1E40AF", label: "SENDING" },
  scheduled: { bg: "#FEF3C7", fg: "#92400E", label: "SCHEDULED" },
  queued:    { bg: "#EFF6FF", fg: "#1E40AF", label: "QUEUED" },
  cancelled: { bg: "#F3F4F6", fg: "#6B7280", label: "CANCELLED" },
  failed:    { bg: "#FEF2F2", fg: "#991B1B", label: "FAILED" },
};

// Email deliverability banner — surfaces silent outages (e.g. unverified
// Resend domain) so admins know before resets/invites/campaigns vanish.
const EMAIL_HEALTH = {
  healthy:      { bg: "#ECFDF5", border: "#10B981", fg: "#065F46", label: "Email delivery healthy" },
  degraded:     { bg: "#FEF3C7", border: "#F59E0B", fg: "#92400E", label: "Email delivery degraded" },
  down:         { bg: "#FEF2F2", border: "#EF4444", fg: "#991B1B", label: "Email delivery DOWN" },
  unconfigured: { bg: "#FEF2F2", border: "#EF4444", fg: "#991B1B", label: "Email not configured" },
  idle:         { bg: "#F3F4F6", border: "#E5E5E5", fg: "#525252", label: "No recent emails" },
};

const EmailHealthBanner = () => {
  const [h, setH] = useState(null);
  const fetchHealth = useCallback(async () => {
    try {
      const res = await api.get("/admin/comms/email-health");
      setH(res.data);
    } catch { /* non-fatal */ }
  }, []);
  useEffect(() => { fetchHealth(); }, [fetchHealth]);
  if (!h) return null;
  const sty = EMAIL_HEALTH[h.status] || EMAIL_HEALTH.idle;
  const bad = h.status === "down" || h.status === "unconfigured" || h.status === "degraded";
  return (
    <div
      className="flex flex-wrap items-center gap-x-3 gap-y-1 border px-3 py-2 text-xs"
      style={{ background: sty.bg, borderColor: sty.border, color: sty.fg }}
      data-testid="email-health-banner"
    >
      {bad ? <AlertTriangle className="w-4 h-4 flex-shrink-0" /> : <Mail className="w-4 h-4 flex-shrink-0" />}
      <span className="font-bold" data-testid="email-health-status">{sty.label}</span>
      <span className="opacity-80">· from {h.from_address}</span>
      <span className="opacity-80">· last {h.window_hours}h: {h.sent} sent / {h.failed} failed / {h.skipped} skipped</span>
      {h.hint && <span className="w-full font-semibold mt-0.5">{h.hint}</span>}
    </div>
  );
};

const buildAudience = (key) => {
  if (key === "approved") return { approval_status: "approved" };
  if (key === "pending") return { approval_status: "pending" };
  if (key === "online") return { is_online: true };
  return {};
};

// Budget dashboard + emergency controls (channel pauses, limits, cancel-all).
const CommsControls = ({ refreshKey }) => {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  const [limits, setLimits] = useState({ daily: "", monthly: "", per: "" });
  const [editingLimits, setEditingLimits] = useState(false);

  const fetchSettings = useCallback(async () => {
    try {
      const res = await api.get("/admin/comms/settings");
      setS(res.data);
      setLimits({
        daily: res.data.daily_limit_gbp, monthly: res.data.monthly_limit_gbp,
        per: res.data.per_campaign_limit_gbp,
      });
    } catch (e) { /* non-fatal */ }
  }, []);
  useEffect(() => { fetchSettings(); }, [fetchSettings, refreshKey]);

  const toggleFlag = async (key) => {
    if (!s) return;
    setBusy(true);
    try {
      const res = await api.post("/admin/comms/settings/flags", { [key]: !s[key] });
      setS((p) => ({ ...p, ...res.data }));
      toast.success("Updated.");
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const cancelAll = async () => {
    setBusy(true);
    try {
      const res = await api.post("/admin/comms/cancel-all");
      toast.success(`Cancelled ${res.data.cancelled} pending campaign(s).`);
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const saveLimits = async () => {
    setBusy(true);
    try {
      const res = await api.post("/admin/comms/settings/limits", {
        daily_limit_gbp: Number(limits.daily),
        monthly_limit_gbp: Number(limits.monthly),
        per_campaign_limit_gbp: Number(limits.per),
      });
      setS((p) => ({ ...p, ...res.data, budget: { ...p.budget, today: { ...p.budget.today, limit: res.data.daily_limit_gbp }, month: { ...p.budget.month, limit: res.data.monthly_limit_gbp } } }));
      setEditingLimits(false);
      toast.success("Spend caps updated.");
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  if (!s) return null;
  const b = s.budget || { today: {}, month: {} };
  const todayPct = Math.min(100, ((b.today.spent || 0) / (b.today.limit || 1)) * 100);
  const monthPct = Math.min(100, ((b.month.spent || 0) / (b.month.limit || 1)) * 100);
  const barColor = (pct) => (pct >= 90 ? "#EF4444" : pct >= 70 ? "#F59E0B" : "#10B981");

  const FlagPill = ({ k, label }) => (
    <button
      onClick={() => toggleFlag(k)}
      disabled={busy}
      className={`px-2.5 py-1 text-[11px] font-semibold border rounded-sm transition-colors ${s[k] ? "bg-[#FEF2F2] border-[#EF4444] text-[#991B1B]" : "border-border text-muted-foreground hover:bg-secondary/50"}`}
      data-testid={`comms-flag-${k}`}
    >
      {label}: {s[k] ? "PAUSED" : "live"}
    </button>
  );

  return (
    <div className="border border-border p-4 space-y-4" data-testid="comms-controls">
      {/* Budget dashboard */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div data-testid="comms-budget-today">
          <div className="flex items-center justify-between text-xs mb-1">
            <span className="label-mini text-muted-foreground">SMS SPEND · TODAY</span>
            <span className="font-mono-tab font-semibold">£{(b.today.spent ?? 0).toFixed(2)} / £{b.today.limit}</span>
          </div>
          <div className="h-2 w-full bg-secondary rounded-full overflow-hidden">
            <div className="h-full rounded-full transition-[width]" style={{ width: `${todayPct}%`, background: barColor(todayPct) }} />
          </div>
        </div>
        <div data-testid="comms-budget-month">
          <div className="flex items-center justify-between text-xs mb-1">
            <span className="label-mini text-muted-foreground">SMS SPEND · THIS MONTH</span>
            <span className="font-mono-tab font-semibold">£{(b.month.spent ?? 0).toFixed(2)} / £{b.month.limit}</span>
          </div>
          <div className="h-2 w-full bg-secondary rounded-full overflow-hidden">
            <div className="h-full rounded-full transition-[width]" style={{ width: `${monthPct}%`, background: barColor(monthPct) }} />
          </div>
        </div>
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-muted-foreground">
        <span>{b.campaigns ?? 0} campaigns</span>
        <span>· {b.messages ?? 0} messages this month</span>
        <span>· per-campaign cap £{s.per_campaign_limit_gbp}</span>
        <button onClick={() => setEditingLimits((v) => !v)} className="ml-auto underline hover:text-foreground" data-testid="comms-edit-limits">
          {editingLimits ? "Close" : "Edit caps"}
        </button>
      </div>

      {editingLimits && (
        <div className="grid grid-cols-3 gap-2 items-end" data-testid="comms-limits-editor">
          {[["daily", "Daily £"], ["monthly", "Monthly £"], ["per", "Per-campaign £"]].map(([k, lbl]) => (
            <div key={k}>
              <Label className="label-mini">{lbl}</Label>
              <Input type="number" min="0" step="1" value={limits[k]} onChange={(e) => setLimits((p) => ({ ...p, [k]: e.target.value }))} data-testid={`comms-limit-${k}`} />
            </div>
          ))}
          <Button size="sm" onClick={saveLimits} disabled={busy} className="col-span-3" data-testid="comms-limits-save">Save caps</Button>
        </div>
      )}

      {/* Emergency controls */}
      <div className="flex flex-wrap items-center gap-2 border-t border-border pt-3">
        <ShieldAlert className={`w-4 h-4 ${s.paused ? "text-[#EF4444]" : "text-muted-foreground"}`} />
        <span className="text-xs font-semibold mr-1">Emergency controls:</span>
        <FlagPill k="paused" label="All processing" />
        <FlagPill k="sms_paused" label="SMS" />
        <FlagPill k="email_paused" label="Email" />
        <FlagPill k="recruitment_paused" label="Recruitment" />
        <Button size="sm" variant="outline" onClick={cancelAll} disabled={busy} className="border-[#EF4444] text-[#EF4444] hover:bg-[#FEF2F2] ml-auto" data-testid="comms-cancel-all">
          <Ban className="w-3.5 h-3.5 mr-1.5" /> Cancel all pending
        </Button>
      </div>
    </div>
  );
};

const ComposeCampaign = ({ onSent }) => {
  const [channel, setChannel] = useState("sms");
  const [audienceKey, setAudienceKey] = useState("approved");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [name, setName] = useState("");
  const [schedule, setSchedule] = useState(false);
  const [scheduledAt, setScheduledAt] = useState("");
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [sending, setSending] = useState(false);
  const [testing, setTesting] = useState(false);
  // Confirmation modal state
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [reviewed, setReviewed] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [adminPassword, setAdminPassword] = useState("");

  const audiencePayload = () => ({
    channel, audience: buildAudience(audienceKey), body: body || undefined,
  });

  const runPreview = useCallback(async () => {
    setPreviewing(true);
    try {
      const res = await api.post("/admin/comms/audience-preview", audiencePayload());
      setPreview(res.data);
    } catch (e) {
      toast.error(formatApiError(e) || "Preview failed");
    } finally {
      setPreviewing(false);
    }
    // eslint-disable-next-line
  }, [channel, audienceKey, body]);
  // Auto-preview on channel / audience change (and debounce body for cost).
  useEffect(() => {
    const t = setTimeout(runPreview, 300);
    return () => clearTimeout(t);
  }, [runPreview]);

  const testSend = async () => {
    if (!body.trim()) { toast.error("Write a message to test."); return; }
    setTesting(true);
    try {
      const res = await api.post("/admin/comms/test-send", {
        channel, subject: subject.trim() || undefined, body: body.trim(),
        audience: buildAudience(audienceKey),
      });
      if (res.data.status === "sent") {
        toast.success(`Test ${channel} sent to you (would target ${res.data.audience_size}).`);
      } else if (res.data.status === "no_admin_contact") {
        toast.error(res.data.detail || "No contact on your admin profile.");
      } else {
        toast.warning(`Test ${channel} status: ${res.data.status} (provider).`);
      }
    } catch (e) {
      toast.error(formatApiError(e) || "Test send failed");
    } finally {
      setTesting(false);
    }
  };

  const openConfirm = () => {
    if (!body.trim()) { toast.error("Message body is required."); return; }
    if (channel === "email" && !subject.trim()) { toast.error("Email subject is required."); return; }
    if (schedule && !scheduledAt) { toast.error("Pick a date/time to schedule."); return; }
    if (preview && preview.eligible === 0) { toast.error("No eligible recipients."); return; }
    setReviewed(false); setConfirmText(""); setAdminPassword("");
    setConfirmOpen(true);
  };

  const requiredPhrase = schedule ? "SCHEDULE" : "SEND";
  const eligible = preview?.eligible ?? 0;
  const cost = preview?.estimated_sms_cost_gbp ?? 0;
  const needPassword = eligible >= (preview?.password_threshold ?? 500) || cost >= (preview?.cost_password_gbp ?? 50);
  const needSuper = eligible >= (preview?.superadmin_threshold ?? 1000) || cost > (preview?.cost_superadmin_gbp ?? 100);
  const isLarge = eligible >= (preview?.warn_threshold ?? 100) || cost >= (preview?.cost_extra_confirm_gbp ?? 10);
  const overBudget = preview?.over_daily_budget || preview?.over_monthly_budget;

  const confirmSend = async () => {
    setSending(true);
    try {
      const payload = {
        channel, body: body.trim(), subject: subject.trim() || undefined,
        audience: buildAudience(audienceKey), name: name.trim() || undefined,
        confirm: reviewed, confirm_text: confirmText.trim().toUpperCase(),
        preview_audience_size: preview?.eligible,
        cost_estimate_gbp: preview?.estimated_sms_cost_gbp,
      };
      if (schedule && scheduledAt) payload.scheduled_at = new Date(scheduledAt).toISOString();
      if (needPassword) payload.admin_password = adminPassword;
      const res = await api.post("/admin/comms/campaigns", payload);
      toast.success(res.data.status === "scheduled" ? "Campaign scheduled." : "Campaign is sending now.");
      setBody(""); setSubject(""); setName(""); setSchedule(false); setScheduledAt("");
      setConfirmOpen(false);
      onSent?.();
    } catch (e) {
      toast.error(formatApiError(e) || "Couldn't create campaign");
    } finally {
      setSending(false);
    }
  };

  const smsSegments = Math.max(1, Math.ceil(body.length / 160));
  const confirmDisabled =
    sending || !reviewed || confirmText.trim().toUpperCase() !== requiredPhrase ||
    (needPassword && !adminPassword);

  return (
    <div className="border border-border p-4 space-y-4" data-testid="comms-compose">
      <div className="flex items-center gap-2">
        <Megaphone className="w-4 h-4" />
        <h3 className="font-heading text-lg font-black tracking-tight">New campaign</h3>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <Label className="label-mini">CHANNEL</Label>
          <div className="flex gap-2 mt-1">
            {[["sms", "SMS", MessageSquare], ["email", "Email", Mail]].map(([v, lbl, Icon]) => (
              <button
                key={v}
                onClick={() => setChannel(v)}
                className={`flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-semibold border transition-colors ${channel === v ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "border-border hover:bg-secondary/50"}`}
                data-testid={`comms-channel-${v}`}
              >
                <Icon className="w-3.5 h-3.5" /> {lbl}
              </button>
            ))}
          </div>
        </div>
        <div>
          <Label className="label-mini">AUDIENCE</Label>
          <Select value={audienceKey} onValueChange={setAudienceKey}>
            <SelectTrigger className="mt-1" data-testid="comms-audience-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {AUDIENCES.map((a) => (
                <SelectItem key={a.value} value={a.value} data-testid={`comms-audience-${a.value}`}>
                  {a.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {preview && (
        <div
          className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs p-2.5 bg-secondary/40 border border-border"
          data-testid="comms-audience-preview"
        >
          <span className="inline-flex items-center gap-1 font-semibold">
            <Users className="w-3.5 h-3.5" /> {preview.eligible} will receive this
          </span>
          <span className="text-muted-foreground">· {preview.total_matched} matched</span>
          {channel === "sms" && body && (
            <span className="text-muted-foreground">· ~{preview.estimated_sms_segments} segments · est. £{preview.estimated_sms_cost_gbp}</span>
          )}
          {preview.excluded_opted_out > 0 && (
            <span className="text-[#92400E]">· {preview.excluded_opted_out} opted-out excluded</span>
          )}
          {preview.missing_contact > 0 && (
            <span className="text-muted-foreground">· {preview.missing_contact} no {channel === "sms" ? "phone" : "email"}</span>
          )}
          <Button variant="ghost" size="sm" onClick={runPreview} disabled={previewing} className="h-6 px-1.5 ml-auto" data-testid="comms-preview-refresh">
            <Eye className="w-3 h-3" />
          </Button>
        </div>
      )}

      <div>
        <Label className="label-mini">CAMPAIGN NAME (OPTIONAL)</Label>
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Weekend shift push" data-testid="comms-name" />
      </div>

      {channel === "email" && (
        <div>
          <Label className="label-mini">SUBJECT</Label>
          <Input value={subject} onChange={(e) => setSubject(e.target.value)} maxLength={200} placeholder="Subject line" data-testid="comms-subject" />
        </div>
      )}

      <div>
        <Label className="label-mini">MESSAGE</Label>
        <Textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          maxLength={2000}
          rows={5}
          placeholder={channel === "sms" ? "Keep it short — drivers read these on the move." : "Write your message…"}
          data-testid="comms-body"
        />
        <div className="text-[11px] text-muted-foreground mt-1">
          {body.length}/2000 chars{channel === "sms" ? ` · ~${smsSegments} SMS segment${smsSegments > 1 ? "s" : ""}` : ""}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <label className="inline-flex items-center gap-1.5 text-sm cursor-pointer" data-testid="comms-schedule-toggle">
          <input type="checkbox" checked={schedule} onChange={(e) => setSchedule(e.target.checked)} />
          <Clock className="w-3.5 h-3.5" /> Schedule for later
        </label>
        {schedule && (
          <Input
            type="datetime-local"
            value={scheduledAt}
            onChange={(e) => setScheduledAt(e.target.value)}
            className="w-auto"
            data-testid="comms-schedule-input"
          />
        )}
        <div className="ml-auto flex items-center gap-2">
          <Button variant="outline" onClick={testSend} disabled={testing} data-testid="comms-test-send-btn">
            <FlaskConical className="w-3.5 h-3.5 mr-1.5" /> {testing ? "Sending…" : "Send test to me"}
          </Button>
          <Button
            onClick={openConfirm}
            disabled={preview && preview.eligible === 0}
            className="bg-[#0A0A0A] hover:bg-[#1F1F1F] text-white"
            data-testid="comms-review-btn"
          >
            <Send className="w-3.5 h-3.5 mr-1.5" />
            Review &amp; {schedule ? "schedule" : "send"}
          </Button>
        </div>
      </div>

      {/* Confirmation modal — required before any live send */}
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent data-testid="comms-confirm-modal">
          <DialogHeader>
            <DialogTitle>Confirm {schedule ? "scheduled " : ""}campaign</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div className="grid grid-cols-2 gap-2 text-xs">
              <Stat label="Recipients" value={eligible} testid="confirm-recipients" />
              <Stat label={channel === "sms" ? "SMS recipients" : "Email recipients"} value={channel === "sms" ? preview?.sms_recipients : preview?.email_recipients} />
              {channel === "sms" && <Stat label="Est. SMS segments" value={preview?.estimated_sms_segments} />}
              {channel === "sms" && <Stat label="Est. cost" value={`£${preview?.estimated_sms_cost_gbp ?? 0}`} testid="confirm-cost" />}
              <Stat label="Opted-out excluded" value={preview?.excluded_opted_out} />
              <Stat label="No contact excluded" value={preview?.missing_contact} />
              {schedule && <Stat label="Scheduled" value={scheduledAt ? new Date(scheduledAt).toLocaleString("en-GB") : "—"} />}
            </div>

            {overBudget && (
              <div className="flex items-start gap-2 text-xs p-2 bg-[#FEF2F2] border border-[#EF4444] text-[#991B1B]" data-testid="comms-budget-warning">
                <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <span>This campaign (est. £{cost}) would exceed your remaining SMS budget. It will be blocked — raise the cap in Emergency controls to proceed.</span>
              </div>
            )}

            {isLarge && (
              <div className="flex items-start gap-2 text-xs p-2 bg-[#FEF3C7] border border-[#F59E0B] text-[#92400E]" data-testid="comms-large-warning">
                <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <span>Large campaign — this will reach <strong>{eligible}</strong> recipients{cost > 0 ? ` at an estimated £${cost}` : ""}. Double-check before proceeding.</span>
              </div>
            )}
            {needSuper && (
              <div className="text-xs p-2 bg-[#FEF2F2] border border-[#EF4444] text-[#991B1B]" data-testid="comms-super-warning">
                {eligible} recipients requires super-admin permission. If you're not a super-admin this will be rejected.
              </div>
            )}

            <label className="flex items-center gap-2 cursor-pointer" data-testid="comms-reviewed-row">
              <Checkbox checked={reviewed} onCheckedChange={(v) => setReviewed(!!v)} data-testid="comms-reviewed-check" />
              <span>I have reviewed the recipient count and wish to proceed.</span>
            </label>

            <div>
              <Label className="label-mini">TYPE {requiredPhrase} TO CONFIRM</Label>
              <Input
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder={requiredPhrase}
                data-testid="comms-confirm-text"
              />
            </div>

            {needPassword && (
              <div data-testid="comms-password-row">
                <Label className="label-mini">CONFIRM WITH YOUR PASSWORD</Label>
                <Input
                  type="password"
                  value={adminPassword}
                  onChange={(e) => setAdminPassword(e.target.value)}
                  placeholder="Required for 500+ recipients"
                  data-testid="comms-admin-password"
                />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConfirmOpen(false)} data-testid="comms-confirm-cancel">Cancel</Button>
            <Button onClick={confirmSend} disabled={confirmDisabled} className="bg-[#0A0A0A] hover:bg-[#1F1F1F] text-white" data-testid="comms-confirm-send">
              {sending ? "Working…" : schedule ? "Schedule campaign" : `Send to ${eligible}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const Stat = ({ label, value, testid }) => (
  <div className="border border-border p-2" data-testid={testid}>
    <div className="label-mini text-muted-foreground">{label}</div>
    <div className="font-heading font-black text-base tabular-nums">{value ?? 0}</div>
  </div>
);

const CampaignHistory = ({ refreshKey }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/comms/campaigns");
      setRows(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      toast.error(formatApiError(e) || "Failed to load campaigns");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { fetchRows(); }, [fetchRows, refreshKey]);

  const cancel = async (id) => {
    try {
      await api.post(`/admin/comms/campaigns/${id}/cancel`);
      toast.success("Campaign cancelled.");
      fetchRows();
    } catch (e) {
      toast.error(formatApiError(e) || "Cancel failed");
    }
  };

  return (
    <div className="space-y-2" data-testid="comms-history">
      <div className="flex items-center justify-between">
        <h3 className="font-heading text-lg font-black tracking-tight">Campaign history</h3>
        <Button variant="outline" size="sm" onClick={fetchRows} disabled={loading} data-testid="comms-history-refresh">
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
        </Button>
      </div>
      {!loading && rows.length === 0 && (
        <div className="border border-dashed border-border p-6 text-center text-sm text-muted-foreground" data-testid="comms-history-empty">
          No campaigns yet. Compose one above.
        </div>
      )}
      {rows.map((c) => {
        const sty = STATUS_STYLE[c.status] || STATUS_STYLE.queued;
        return (
          <div key={c.id} className="border border-border p-3" data-testid={`campaign-row-${c.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  {c.channel === "sms" ? <MessageSquare className="w-3.5 h-3.5" /> : <Mail className="w-3.5 h-3.5" />}
                  <span className="font-semibold text-sm truncate">{c.name || c.subject || `${c.channel.toUpperCase()} campaign`}</span>
                  <span className="px-1.5 py-0.5 text-[10px] font-bold rounded-sm" style={{ background: sty.bg, color: sty.fg }} data-testid={`campaign-status-${c.id}`}>
                    {sty.label}
                  </span>
                </div>
                <div className="text-[11px] text-muted-foreground mt-0.5 truncate">{c.body}</div>
                <div className="text-[11px] text-muted-foreground mt-1 font-mono-tab">
                  {c.counts?.sent ?? 0} sent · {c.counts?.failed ?? 0} failed · {c.counts?.skipped ?? 0} skipped
                  {c.scheduled_at ? ` · scheduled ${new Date(c.scheduled_at).toLocaleString("en-GB")}` : ""}
                </div>
              </div>
              {(c.status === "scheduled" || c.status === "queued") && (
                <Button size="sm" variant="outline" onClick={() => cancel(c.id)} className="border-[#EF4444] text-[#EF4444] hover:bg-[#FEF2F2] flex-shrink-0" data-testid={`campaign-cancel-${c.id}`}>
                  <XCircle className="w-3.5 h-3.5 mr-1" /> Cancel
                </Button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

const RecruitmentInvites = () => {
  const [channel, setChannel] = useState("email");
  const [raw, setRaw] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  // Each line: "Name, email, phone" (email/phone optional individually).
  const parse = () =>
    raw.split("\n").map((l) => l.trim()).filter(Boolean).map((line) => {
      const [name, email, phone] = line.split(",").map((s) => (s || "").trim());
      return { name, email, phone };
    });

  const send = async () => {
    const recipients = parse();
    if (recipients.length === 0) { toast.error("Add at least one recipient."); return; }
    setSending(true);
    try {
      const res = await api.post("/admin/recruitment/invite", {
        channel, recipients, message: message.trim() || undefined,
      });
      setResult(res.data);
      toast.success(`Invites processed — ${res.data.sent} sent, ${res.data.failed} failed.`);
    } catch (e) {
      toast.error(formatApiError(e) || "Invite send failed");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="border border-border p-4 space-y-4" data-testid="recruitment-panel">
      <div className="flex items-center gap-2">
        <Users className="w-4 h-4" />
        <h3 className="font-heading text-lg font-black tracking-tight">Invite drivers</h3>
      </div>
      <p className="text-xs text-muted-foreground">
        One recipient per line as <code className="font-mono-tab">Name, email, phone</code>. They'll receive a signup link.
      </p>
      <div>
        <Label className="label-mini">CHANNEL</Label>
        <div className="flex gap-2 mt-1">
          {[["email", "Email"], ["sms", "SMS"], ["both", "Both"]].map(([v, lbl]) => (
            <button
              key={v}
              onClick={() => setChannel(v)}
              className={`flex-1 px-3 py-2 text-sm font-semibold border transition-colors ${channel === v ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "border-border hover:bg-secondary/50"}`}
              data-testid={`recruit-channel-${v}`}
            >
              {lbl}
            </button>
          ))}
        </div>
      </div>
      <div>
        <Label className="label-mini">RECIPIENTS</Label>
        <Textarea
          value={raw}
          onChange={(e) => setRaw(e.target.value)}
          rows={5}
          placeholder={"Jane Smith, jane@example.com, 07700900123\nJohn Doe, john@example.com,"}
          data-testid="recruit-recipients"
        />
      </div>
      {(channel === "sms" || channel === "both") && (
        <div>
          <Label className="label-mini">SMS NOTE (OPTIONAL)</Label>
          <Input value={message} onChange={(e) => setMessage(e.target.value)} maxLength={320} placeholder="Short personal note prepended to the SMS" data-testid="recruit-message" />
        </div>
      )}
      <div className="flex justify-end">
        <Button onClick={send} disabled={sending} className="bg-[#0A0A0A] hover:bg-[#1F1F1F] text-white" data-testid="recruit-send-btn">
          <Send className="w-3.5 h-3.5 mr-1.5" /> {sending ? "Sending…" : "Send invites"}
        </Button>
      </div>
      {result && (
        <div className="text-xs border-t border-border pt-3" data-testid="recruit-result">
          <div className="font-semibold mb-1">{result.sent} sent · {result.failed} failed · {result.skipped} skipped</div>
          <div className="space-y-0.5">
            {result.results.map((r, i) => (
              <div key={i} className="text-muted-foreground font-mono-tab">
                {r.name} — {r.email_status ? `email:${r.email_status}` : ""} {r.sms_status ? `sms:${r.sms_status}` : ""}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export const CommsPanel = ({ initialTab = "campaigns" }) => {
  const [refreshKey, setRefreshKey] = useState(0);
  return (
    <div className="space-y-4" data-testid="comms-panel">
      <div>
        <h2 className="font-heading text-2xl font-black tracking-tight">Communications</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Send bulk SMS / email to your fleet and invite new drivers. Opted-out
          recipients are automatically excluded.
        </p>
      </div>
      <Tabs defaultValue={initialTab}>
        <TabsList data-testid="comms-tabs">
          <TabsTrigger value="campaigns" data-testid="comms-tab-campaigns">Campaigns</TabsTrigger>
          <TabsTrigger value="recruitment" data-testid="comms-tab-recruitment">Recruitment</TabsTrigger>
        </TabsList>
        <TabsContent value="campaigns" className="space-y-4 mt-4">
          <EmailHealthBanner />
          <CommsControls refreshKey={refreshKey} />
          <ComposeCampaign onSent={() => setRefreshKey((k) => k + 1)} />
          <CampaignHistory refreshKey={refreshKey} />
        </TabsContent>
        <TabsContent value="recruitment" className="mt-4">
          <RecruitmentInvites />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default CommsPanel;
